import type {
  Company,
  Deal,
  ImageItem,
  Lead,
  Section,
  SimpleHouse,
  Unit,
  User,
} from '../../types';

import { cache } from '../cache';
import { ApiResponse, estClient } from './estClient';
import { NotFoundError } from './httpClient';

export interface UnitStatus {
  id: string;
  name: string;
  days: string;

  /**
   * - '1' — closed
   * - '2' — free
   * - '3' — reserved
   * - '4' — sold
   */
  type: string;
  lock: '1' | '0';
  created_at: string | null;
}

export interface UnitImage {
  id: string;
  unit_id: string;
  image_id: string;

  /**
   * '1' - MASTER_PLAN
   * '2' - FLOOR_PLAN
   * '3' - UNIT_PLAN
   * '4' - UNIT_VISUAL
   */
  image_type: '1' | '2' | '3' | '4';

  image: ImageItem;
}

interface GoogleFolderResponse extends ApiResponse {
  code: string;

  folder: {
    /**
     * Folder id in google drive
     */
    id: string;

    /**
     * A path to the folder. Can be shown to user but useless when creating a link to google drive
     */
    path: string;
  };
}

export interface RichUnit extends Unit {
  section: Section & {
    house: SimpleHouse;
  };
  company: Company;
  unit_images: UnitImage[];
  standard_exchange_rates: unknown[];
  labels: unknown[];
  tasks: unknown[];
}

export interface SecureUnit {
  unit_id: string;
  custom_fields: CustomField[];
  lead_unit_assn: {
    id: string;
    unit_id: string;
    lead_id: string;
    deal_id: string;

    /**
     * @example '2017-08-08 22:23:33'
     */
    created_at: string;
    created_by: string;

    /**
     * @example '2017-08-08 22:23:33'
     */
    updated_at: string;
    updated_by: string;
    status: '1';
    type: '2';

    /**
     * @example '2017-08-08 22:23:33'
     */
    due_date: string | null;

    is_fast_deal: '0' | '1';

    deal: Deal;
    manager: User;
    lead: Lead;
  };
}

interface CustomField {
  name: string;
  value: string;
}

class UnitApi {
  async getById(id: string): Promise<RichUnit | null> {
    try {
      return estClient.get<RichUnit>(`/api/unit/get/${id}`);
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getByIdSecure(id: string): Promise<SecureUnit | null> {
    try {
      return estClient.get<SecureUnit>(`/api/unit/get_secure/${id}`);
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getStatuses(): Promise<UnitStatus[]> {
    return cache.get('unit-statuses', () =>
      estClient.get<UnitStatus[]>('/api/unit/status'),
    );
  }

  async update(
    id: string,
    body: {
      area_total: string;
      is_price_per_meter: number | 0 | 1;
      price: string;
      currency: string | null;
      exchange_rate_declared: string | null;
    },
  ): Promise<RichUnit | null> {
    try {
      return estClient.post<RichUnit>(`/api/unit/${id}/update_params`, {
        body,
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async getDocsDirUrl(unitId: string) {
    const resp = await estClient.get<GoogleFolderResponse>(
      `/api/unit/${unitId}/google_folder`,
    );

    return `https://drive.google.com/drive/u/0/folders/${resp.folder.id}`;
  }
}

export const unitApi = new UnitApi();
